import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.ts";
import { day, money, rel } from "../format.ts";
import { Empty } from "../ui.tsx";

type Item = { bookingId: string; code: string; title: { fr: string } | null; guest: string | null; kind: string; severity: "urgent" | "soon" | "info"; at: string | null };
type Stay = { bookingId: string; code: string; title: { fr: string } | null; guest: string | null; nights: number; guests: number; checkIn: string; checkOut: string };
interface Dash {
  today: string;
  counts: { requested: number; awaitingPayment: number; confirmed: number; inStay: number };
  attention: Item[]; arrivals: Stay[]; departures: Stay[]; inHouse: Stay[];
  waiting: { paymentsToVerify: number; refundsOwed: number; payoutsDue: number; messagesToSend: number };
  month: { from: string; bookings: number; grossMinor: number; revenueMinor: number; currency: string };
}

const WHAT: Record<string, string> = {
  confirm_availability: "Confirmer la disponibilité auprès du propriétaire",
  verify_payment: "Vérifier la preuve de paiement",
  payment_deadline: "Délai de paiement",
  arrival: "Arrivée",
  overdue_departure: "Départ à clôturer",
};
const SEV: Record<string, string> = { urgent: "Urgent", soon: "Bientôt", info: "À prévoir" };

function Tile({ n, label, to }: { n: number; label: string; to: string }) {
  return <Link to={to} className={`tile ${n > 0 ? "hot" : ""}`}><b className="num">{n}</b><span className="eyebrow">{label}</span></Link>;
}

function StayList({ title, rows, empty }: { title: string; rows: Stay[]; empty: string }) {
  return (
    <div className="card">
      <b>{title} <span className="muted num">({rows.length})</span></b>
      {rows.length === 0 ? <p className="muted small">{empty}</p> : (
        <ul className="rows" style={{ borderTop: 0 }}>
          {rows.map((s) => (
            <li key={s.bookingId} className="kv"><Link to={`/b/${s.bookingId}`} className="link"><span className="code num">{s.code}</span> {s.guest}</Link><span className="muted small">{s.title?.fr} · {s.guests} pers.</span></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Dashboard() {
  const [d, setD] = useState<Dash | null>(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => { api<Dash>("/v1/staff/dashboard").then((x) => { setD(x); setErr(false); }).catch(() => setErr(true)); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  if (err && !d) return <Empty>Impossible de charger le tableau de bord.</Empty>;
  if (!d) return <div className="skel" aria-hidden="true" />;
  const urgent = d.attention.filter((a) => a.severity === "urgent").length;

  return (
    <section>
      <header className="pagehead">
        <span className="eyebrow">{day(d.today)}</span>
        <h1>Tableau de bord</h1>
        <p className="muted small">{urgent > 0 ? `${urgent} point${urgent > 1 ? "s" : ""} urgent${urgent > 1 ? "s" : ""}.` : "Rien d'urgent."} Actualisé automatiquement chaque minute.</p>
      </header>

      <div className="tiles">
        <Tile n={d.counts.requested} label="À confirmer" to="/q/requested" />
        <Tile n={d.waiting.paymentsToVerify} label="Preuves à vérifier" to="/q/verify" />
        <Tile n={d.counts.awaitingPayment} label="Paiement attendu" to="/q/awaiting_payment" />
        <Tile n={d.counts.confirmed} label="Confirmés" to="/q/confirmed" />
        <Tile n={d.counts.inStay} label="En séjour" to="/q/in_stay" />
        <Tile n={d.waiting.payoutsDue} label="Versements" to="/payouts" />
        <Tile n={d.waiting.refundsOwed} label="Remboursements" to="/refunds" />
        <Tile n={d.waiting.messagesToSend} label="Messages à envoyer" to="/messages" />
      </div>

      <div className="grid2" style={{ marginTop: 32 }}>
        <div className="stackc">
          <div className="card">
            <b>À traiter maintenant <span className="muted num">({d.attention.length})</span></b>
            {d.attention.length === 0 ? <p className="muted small">Tout est à jour.</p> : (
              <ul className="rows" style={{ borderTop: 0 }}>
                {d.attention.map((a, i) => (
                  <li key={`${a.bookingId}-${a.kind}-${i}`} className={`attn ${a.severity}`}>
                    <Link to={`/b/${a.bookingId}`} className="link">
                      <span className="sev">{SEV[a.severity]}</span>
                      <b>{WHAT[a.kind] ?? a.kind}</b>
                      <span className="muted small"><span className="code num">{a.code}</span> · {a.guest ?? "—"} · {a.title?.fr ?? ""}{a.at ? ` · ${a.kind === "arrival" || a.kind === "overdue_departure" ? day(a.at.slice(0, 10)) : rel(a.at)}` : ""}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <StayList title="Arrivées aujourd'hui" rows={d.arrivals} empty="Aucune arrivée aujourd'hui." />
          <StayList title="Départs aujourd'hui" rows={d.departures} empty="Aucun départ aujourd'hui." />
        </div>
        <div className="stackc">
          <div className="card">
            <b>Ce mois-ci</b>
            <div className="kv"><span className="muted">Séjours confirmés ou terminés</span><b className="num">{d.month.bookings}</b></div>
            <div className="kv"><span className="muted">Volume encaissé</span><b className="num">{money(d.month.grossMinor, d.month.currency)}</b></div>
            <div className="kv"><span className="muted">Revenu Sekani</span><b className="num">{money(d.month.revenueMinor, d.month.currency)}</b></div>
          </div>
          <StayList title="Actuellement en séjour" rows={d.inHouse} empty="Personne en séjour." />
        </div>
      </div>
    </section>
  );
}
